/* 
* Copyright (c) 2026 Send2Me, media capture and delivery platform. All rights reserved. 
* Distributed under the TruVideo proprietary license. 
*/
import axios from 'axios';
import { getSessionToken, setSessionToken } from '../utils/cookieService';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001';
const SESSION_HEADER = 'x-session-token';

/**
 * Create a specialized axios instance for capture API calls
 * This client automatically handles session tokens via cookies and headers
 */
const captureClient = axios.create({
  baseURL: `${API_URL}/api/capture`,
  timeout: 30000,
  headers: {
    'Content-Type': 'application/json'
  }
});

/**
 * Request interceptor to add session token and slug headers
 */
captureClient.interceptors.request.use(
  (config) => {
    // Extract token from URL (assuming format: /capture/:token/...)
    const tokenMatch = config.url?.match(/^\/([^/]+)/);
    if (tokenMatch && tokenMatch[1]) {
      const assignmentToken = tokenMatch[1];
      const sessionToken = getSessionToken(assignmentToken);
      
      if (sessionToken) {
        config.headers[SESSION_HEADER] = sessionToken;
      }
    }

    // Inject the subdomain slug so the backend can validate it
    const hostname = window.location.hostname;
    const slug = hostname.includes('.') ? hostname.split('.')[0] : '';
    if (slug) {
      config.headers['x-slug'] = slug;
    }
    
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

/**
 * Response interceptor to handle session token from responses
 */
captureClient.interceptors.response.use(
  (response) => {
    // If the response contains a sessionToken, store it in cookie
    if (response.data?.sessionToken) {
      const tokenMatch = response.config.url?.match(/^\/([^/]+)/);
      if (tokenMatch && tokenMatch[1]) {
        const assignmentToken = tokenMatch[1];
        setSessionToken(assignmentToken, response.data.sessionToken);
      }
    }

    return response;
  },
  (error) => {
    // Handle session conflict specially - don't treat as regular session error
    if (error.response?.status === 409 && error.response?.data?.sessionConflict) {
      if (error.config?.method !== 'get') {
        // Mid-session conflict (POST upload/start/etc.) — notify Capture.jsx via
        window.dispatchEvent(new CustomEvent('capture-session-conflict'));
      }
      return Promise.reject(error);
    }

    // Handle session-specific errors
    const errorType = error.response?.data?.type;

    if (errorType) {
      // Map session errors to user-friendly messages
      const sessionErrors = {
        'session_expired': 'Your session has expired due to inactivity. Please reload the page.',
        'session_conflict': 'This capture link is already open on another device. Please close it there first.',
        'session_mismatch': 'This capture link is already open on another device.',
        'session_inactive': 'Your session is no longer active. Please reload the page.',
        'no_session': 'No active session found. Please reload the page.',
        'no_session_record': 'No session record found for this capture link.',
      };

      if (sessionErrors[errorType]) {
        error.sessionError = true;
        error.userMessage = sessionErrors[errorType];
      }
    }

    return Promise.reject(error);
  }
);

/**
 * Helper function to check if an error is a session error
 * @param {Error} error - The error to check
 * @returns {boolean} True if the error is session-related
 */
export const isSessionError = (error) => {
  return error?.sessionError === true;
};

/**
 * Helper function to get user-friendly error message
 * @param {Error} error - The error object
 * @returns {string} User-friendly error message
 */
export const getErrorMessage = (error) => {
  return error?.userMessage || error.response?.data?.message || 'An error occurred. Please try again.';
};

export default captureClient;
